import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, DollarSign, CreditCard, Banknote, ArrowRight, ChevronLeft, ChevronRight,
  Plus, X, Loader2, AlertCircle, Truck, CheckCircle2,
} from 'lucide-react';
import { pagosProveedoresService } from '@/services/pagos-proveedores.service';
import { proveedoresService } from '@/services/proveedores.service';
import { comprasService } from '@/services/compras.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';

const methodLabels: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  TRANSFER: 'Transferencia',
  CREDIT: 'Crédito',
};

const methodIcons: Record<string, any> = {
  CASH: Banknote,
  CARD: CreditCard,
  TRANSFER: ArrowRight,
  CREDIT: DollarSign,
};

type Tab = 'pending' | 'history';

interface PayableRow {
  id: string;
  folio: string;
  supplierId: string;
  supplierName: string;
  grandTotal: number;
  paid: number;
  remaining: number;
  receivedAt?: string;
  createdAt?: string;
}

export function SupplierPaymentsPage({ defaultTab = 'pending' }: { defaultTab?: Tab }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [payments, setPayments] = useState<any[]>([]);
  const [payables, setPayables] = useState<PayableRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingPayables, setLoadingPayables] = useState(false);
  const [search, setSearch] = useState('');
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();
  const limit = 20;

  const [showForm, setShowForm] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    paymentMethod: 'TRANSFER',
    reference: '',
    notes: '',
  });

  const totalDebt = useMemo(
    () => payables.reduce((s, p) => s + p.remaining, 0),
    [payables],
  );

  useEffect(() => {
    if (user?.company?.id) {
      loadPayables();
      loadPayments();
    }
  }, [user]);

  useEffect(() => {
    if (tab === 'history') loadPayments();
  }, [page, tab]);

  useEffect(() => {
    if (showForm && user?.company?.id) {
      proveedoresService.findAll(user.company.id).then(setSuppliers).catch(() => {});
    }
  }, [showForm, user]);

  useEffect(() => {
    if (selectedSupplierId && user?.company?.id) {
      comprasService.findAll({
        companyId: user.company.id,
        supplierId: selectedSupplierId,
        status: 'RECEIVED',
        limit: 100,
      }).then(async (res) => {
        const list = res.data || [];
        // Filtrar solo las que aún tienen saldo (si podemos)
        const withBalance: any[] = [];
        for (const o of list) {
          const paid = payments
            .filter((p) => p.purchaseOrderId === o.id || p.purchaseOrder?.id === o.id)
            .reduce((s, p) => s + Number(p.amount || 0), 0);
          const remaining = Number(o.grandTotal || 0) - paid;
          if (remaining > 0.009 || Number(o.grandTotal || 0) === 0) {
            withBalance.push({ ...o, remaining: remaining > 0 ? remaining : Number(o.grandTotal || 0) });
          } else {
            withBalance.push({ ...o, remaining });
          }
        }
        setOrders(withBalance.filter((o) => Number(o.remaining) > 0.009 || Number(o.grandTotal) > 0));
      }).catch(() => setOrders([]));
    } else {
      setOrders([]);
    }
    setSelectedOrderId('');
  }, [selectedSupplierId, user, payments]);

  const loadPayables = async () => {
    if (!user?.company?.id) return;
    setLoadingPayables(true);
    try {
      const [ordersRes, paymentsRes] = await Promise.all([
        comprasService.findAll({
          companyId: user.company.id,
          status: 'RECEIVED',
          limit: 200,
        }),
        pagosProveedoresService.findAll({
          companyId: user.company.id,
          limit: 500,
          page: 1,
        }),
      ]);
      const ordersList = ordersRes.data || [];
      const pays = paymentsRes.data || [];
      setPayments(pays);

      const rows: PayableRow[] = ordersList.map((o: any) => {
        const paid = pays
          .filter((p: any) => p.purchaseOrderId === o.id || p.purchaseOrder?.id === o.id)
          .reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
        const grandTotal = Number(o.grandTotal || 0);
        const remaining = Math.max(0, grandTotal - paid);
        return {
          id: o.id,
          folio: o.folio,
          supplierId: o.supplierId || o.supplier?.id,
          supplierName: o.supplier?.name || 'Proveedor',
          grandTotal,
          paid,
          remaining,
          receivedAt: o.receivedAt,
          createdAt: o.createdAt,
        };
      }).filter((r: PayableRow) => r.remaining > 0.009);

      setPayables(rows);
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoadingPayables(false);
    }
  };

  const loadPayments = async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const result = await pagosProveedoresService.findAll({
        companyId: user.company.id,
        page,
        limit,
      });
      setPayments(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) {
      handleApiError(err);
    }
    setLoading(false);
  };

  const openPayFor = (row: PayableRow) => {
    setSelectedSupplierId(row.supplierId);
    setSelectedOrderId(row.id);
    setPaymentForm({
      amount: row.remaining,
      paymentMethod: 'TRANSFER',
      reference: '',
      notes: '',
    });
    setShowForm(true);
  };

  const handleCreatePayment = async () => {
    if (!user?.company?.id || !selectedSupplierId || !selectedOrderId || paymentForm.amount <= 0) return;
    setSavingPayment(true);
    try {
      await pagosProveedoresService.create({
        companyId: user.company.id,
        supplierId: selectedSupplierId,
        purchaseOrderId: selectedOrderId,
        amount: paymentForm.amount,
        paymentMethod: paymentForm.paymentMethod,
        reference: paymentForm.reference || undefined,
        notes: paymentForm.notes || undefined,
        createdById: user.id,
      });
      setShowForm(false);
      setSelectedSupplierId('');
      setSelectedOrderId('');
      setPaymentForm({ amount: 0, paymentMethod: 'TRANSFER', reference: '', notes: '' });
      await Promise.all([loadPayables(), loadPayments()]);
      await dialogs.alert('Pago registrado correctamente', 'Listo');
    } catch {
      await dialogs.alert('Error al registrar pago');
    } finally {
      setSavingPayment(false);
    }
  };

  const filteredPayables = search
    ? payables.filter(
        (p) =>
          p.folio?.toLowerCase().includes(search.toLowerCase()) ||
          p.supplierName?.toLowerCase().includes(search.toLowerCase()),
      )
    : payables;

  const filteredPayments = search
    ? payments.filter(
        (p) =>
          p.supplier?.name?.toLowerCase().includes(search.toLowerCase()) ||
          p.purchaseOrder?.folio?.toLowerCase().includes(search.toLowerCase()) ||
          p.reference?.toLowerCase().includes(search.toLowerCase()),
      )
    : payments;

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={() => { loadPayables(); loadPayments(); }} onLogin={handleAuthRedirect} />
      {!error && (
        <>
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold">Cuentas por pagar</h1>
              <p className="text-sm text-ios-gray mt-1">
                Aquí ves lo que debes a proveedores y registras los pagos
              </p>
            </div>
            <button onClick={() => setShowForm(true)} className="ios-button flex items-center gap-2 px-4 py-2">
              <Plus size={16} />
              Registrar pago
            </button>
          </div>

          {/* KPI deuda */}
          <div className="ios-card p-4 mb-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
                <AlertCircle size={20} />
              </div>
              <div>
                <p className="text-xs text-ios-gray uppercase tracking-wide">Total por pagar</p>
                <p className="text-2xl font-bold text-amber-600">${totalDebt.toFixed(2)}</p>
              </div>
            </div>
            <p className="text-sm text-ios-gray">
              {payables.length === 0
                ? 'No hay deudas pendientes'
                : `${payables.length} orden${payables.length === 1 ? '' : 'es'} con saldo pendiente`}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 bg-ios-gray-6 p-1 rounded-[12px] w-fit">
            <button
              onClick={() => setTab('pending')}
              className={`px-4 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                tab === 'pending' ? 'bg-white shadow-sm text-foreground' : 'text-ios-gray hover:text-foreground'
              }`}
            >
              Por pagar ({payables.length})
            </button>
            <button
              onClick={() => setTab('history')}
              className={`px-4 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                tab === 'history' ? 'bg-white shadow-sm text-foreground' : 'text-ios-gray hover:text-foreground'
              }`}
            >
              Historial de pagos
            </button>
          </div>

          <div className="relative mb-4 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === 'pending' ? 'Buscar folio o proveedor...' : 'Buscar pago...'}
              className="ios-input w-full pl-9 h-9 text-sm"
            />
          </div>

          {tab === 'pending' ? (
            <div className="ios-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase">Folio OC</th>
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase">Proveedor</th>
                      <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase">Total OC</th>
                      <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase">Pagado</th>
                      <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase">Por pagar</th>
                      <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ios-gray-5">
                    {loadingPayables ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12">
                          <div className="w-6 h-6 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : filteredPayables.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 px-4">
                          <CheckCircle2 size={32} className="mx-auto text-ios-green mb-2" />
                          <p className="text-sm font-medium">No hay nada por pagar</p>
                          <p className="text-xs text-ios-gray mt-1 max-w-md mx-auto">
                            Cuando recibas mercancía de una orden de compra, aquí aparecerá la deuda
                            para que puedas pagarla con un clic.
                          </p>
                          <button
                            onClick={() => navigate('/purchases')}
                            className="mt-4 text-sm text-ios-blue hover:underline inline-flex items-center gap-1"
                          >
                            <Truck size={14} /> Ir a Compras
                          </button>
                        </td>
                      </tr>
                    ) : (
                      filteredPayables.map((row) => (
                        <tr key={row.id} className="hover:bg-ios-gray-6/30 transition-colors">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => navigate(`/purchases/${row.id}`)}
                              className="font-mono text-xs bg-ios-gray-6 px-1.5 py-0.5 rounded hover:bg-ios-blue/10 hover:text-ios-blue"
                            >
                              {row.folio}
                            </button>
                          </td>
                          <td className="px-4 py-3 font-medium">{row.supplierName}</td>
                          <td className="px-4 py-3 text-right">${row.grandTotal.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-ios-green">${row.paid.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-bold text-amber-600">${row.remaining.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => openPayFor(row)}
                              className="ios-button text-xs px-3 py-1.5 inline-flex items-center gap-1"
                            >
                              <DollarSign size={14} /> Pagar
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="ios-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase">Fecha</th>
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase">Proveedor</th>
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase">OC</th>
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase">Método</th>
                      <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase">Monto</th>
                      <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase">Referencia</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ios-gray-5">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12">
                          <div className="w-6 h-6 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : filteredPayments.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-ios-gray text-sm px-4">
                          Aún no hay pagos registrados. Ve a la pestaña <strong>Por pagar</strong> y pulsa Pagar.
                        </td>
                      </tr>
                    ) : (
                      filteredPayments.map((p: any) => {
                        const Icon = methodIcons[p.paymentMethod] || DollarSign;
                        return (
                          <tr key={p.id} className="hover:bg-ios-gray-6/30 transition-colors">
                            <td className="px-4 py-3 text-xs text-ios-gray">
                              {new Date(p.paidAt || p.createdAt).toLocaleDateString('es-MX', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </td>
                            <td className="px-4 py-3 font-medium">{p.supplier?.name || '-'}</td>
                            <td className="px-4 py-3">
                              {p.purchaseOrder?.folio || p.purchaseOrderId ? (
                                <code className="text-xs bg-ios-gray-6 px-1.5 py-0.5 rounded font-mono">
                                  {p.purchaseOrder?.folio || p.purchaseOrderId}
                                </code>
                              ) : (
                                <span className="text-ios-gray text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1 text-xs">
                                <Icon size={12} className="text-ios-gray" />
                                {methodLabels[p.paymentMethod] || p.paymentMethod}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-ios-green">
                              ${Number(p.amount).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-xs text-ios-gray">{p.reference || '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-ios-gray-5">
                  <span className="text-xs text-ios-gray">
                    Página {page} de {totalPages} · {total} pagos
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showForm && (
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl">
                <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
                  <h3 className="font-semibold flex items-center gap-2">
                    <DollarSign size={18} className="text-ios-blue" /> Registrar pago a proveedor
                  </h3>
                  <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                    <X size={18} />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  <div>
                    <label className="text-xs text-ios-gray mb-1 block">Proveedor</label>
                    <select
                      value={selectedSupplierId}
                      onChange={(e) => setSelectedSupplierId(e.target.value)}
                      className="ios-input w-full"
                    >
                      <option value="">Seleccionar proveedor...</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedSupplierId && (
                    <div>
                      <label className="text-xs text-ios-gray mb-1 block">Orden de compra recibida</label>
                      {orders.length === 0 ? (
                        <p className="text-sm text-ios-gray italic px-3 py-2 bg-ios-gray-6 rounded-[10px]">
                          No hay órdenes recibidas pendientes de pago para este proveedor
                        </p>
                      ) : (
                        <select
                          value={selectedOrderId}
                          onChange={(e) => {
                            setSelectedOrderId(e.target.value);
                            const order = orders.find((o) => o.id === e.target.value);
                            if (order) {
                              setPaymentForm((prev) => ({
                                ...prev,
                                amount: Number(order.remaining ?? order.grandTotal) || 0,
                              }));
                            }
                          }}
                          className="ios-input w-full"
                        >
                          <option value="">Seleccionar orden...</option>
                          {orders.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.folio} — por pagar ${Number(o.remaining ?? o.grandTotal).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}

                  {selectedOrderId && (
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div>
                        <label className="text-xs text-ios-gray mb-1 block">Monto a pagar</label>
                        <input
                          type="number"
                          value={paymentForm.amount}
                          onChange={(e) =>
                            setPaymentForm((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))
                          }
                          className="ios-input w-full text-sm font-semibold text-ios-green"
                          step="0.01"
                          min="0.01"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-ios-gray mb-1 block">Método</label>
                        <select
                          value={paymentForm.paymentMethod}
                          onChange={(e) =>
                            setPaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))
                          }
                          className="ios-input w-full text-sm"
                        >
                          <option value="TRANSFER">Transferencia</option>
                          <option value="CASH">Efectivo</option>
                          <option value="CARD">Tarjeta</option>
                          <option value="CREDIT">Crédito</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-ios-gray mb-1 block">Referencia (opcional)</label>
                        <input
                          type="text"
                          value={paymentForm.reference}
                          onChange={(e) =>
                            setPaymentForm((prev) => ({ ...prev, reference: e.target.value }))
                          }
                          className="ios-input w-full text-sm"
                          placeholder="Folio SPEI, cheque..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-ios-gray-5 flex gap-2">
                  <button
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreatePayment}
                    disabled={
                      savingPayment || !selectedSupplierId || !selectedOrderId || paymentForm.amount <= 0
                    }
                    className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50 flex justify-center items-center gap-2"
                  >
                    {savingPayment ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Guardando
                      </>
                    ) : (
                      'Guardar pago'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
